import { useDroppable } from "@dnd-kit/core";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import type { Vessel } from "@/hooks/useVessels";
import type { VesselAssignmentWithRelations } from "@/hooks/useVesselAssignments";
import { AssignmentBar } from "./AssignmentBar";

interface VesselRowProps {
  vessel: Vessel;
  assignments: VesselAssignmentWithRelations[];
  startDate: Date;
  endDate: Date;
  dayWidth: number;
  rowHeight: number;
  onAssignmentSelect: (assignment: VesselAssignmentWithRelations) => void;
  onAssignmentEditRequest: (assignment: VesselAssignmentWithRelations, rect: DOMRect) => void;
  selectedAssignmentId?: string | null;
  onAssignmentResize?: (assignmentId: string, newEndDate: string) => void;
  onAssignmentResizeStart?: (assignmentId: string, newStartDate: string) => void;
  onMoveToOnDeck?: (assignment: VesselAssignmentWithRelations) => void;
  onAssignmentContextMenu?: (assignment: VesselAssignmentWithRelations, x: number, y: number) => void;
  activeDragId?: string | null;
  colorMap?: Record<string, string>;
  nameMap?: Record<string, string>;
  connectingFromId?: string | null;
  onCompleteConnection?: (targetAssignmentId: string) => void;
  /** Customer portal: bars can't be dragged. */
  readOnly?: boolean;
  showNotes?: boolean;
}

export function VesselRow({
  vessel,
  assignments,
  startDate,
  endDate,
  dayWidth,
  rowHeight,
  onAssignmentSelect,
  onAssignmentEditRequest,
  selectedAssignmentId,
  onAssignmentResize,
  onAssignmentResizeStart,
  onMoveToOnDeck,
  onAssignmentContextMenu,
  activeDragId,
  colorMap = {},
  nameMap = {},
  connectingFromId,
  onCompleteConnection,
  readOnly = false,
  showNotes = false,
}: VesselRowProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: `vessel-${vessel.id}`,
    data: { vesselId: vessel.id, vessel },
  });

  // Compute split roles for same-day turnover pairs only.
  // When assignment A ends on the exact same day B starts, A gets 'split-out' (left half of that day)
  // and B gets 'split-in' (right half). All other assignments render full-width (null).
  const splitRoles = useMemo<Map<string, 'split-out' | 'split-in'>>(() => {
    const map = new Map<string, 'split-out' | 'split-in'>();
    for (let i = 0; i < assignments.length; i++) {
      for (let j = i + 1; j < assignments.length; j++) {
        const a = assignments[i];
        const b = assignments[j];
        const aEnd = a.projected_empty_date || a.actual_empty_date;
        const bEnd = b.projected_empty_date || b.actual_empty_date;
        if (aEnd && aEnd === b.fill_date) {
          map.set(a.id, 'split-out');
          map.set(b.id, 'split-in');
        } else if (bEnd && bEnd === a.fill_date) {
          map.set(b.id, 'split-out');
          map.set(a.id, 'split-in');
        }
      }
    }
    return map;
  }, [assignments]);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "relative border-b transition-all duration-200 ease-out",
        isOver && "bg-primary/5 scale-[1.002]"
      )}
      style={{ height: rowHeight }}
    >
      {/* Timeline area with assignments */}
      <div className="absolute inset-0">
        {assignments.map((assignment) => (
          <AssignmentBar
            key={assignment.id}
            assignment={assignment}
            startDate={startDate}
            endDate={endDate}
            dayWidth={dayWidth}
            rowHeight={rowHeight}
            onSelect={() => {
              if (connectingFromId && connectingFromId !== assignment.id) {
                onCompleteConnection?.(assignment.id);
              } else {
                onAssignmentSelect(assignment);
              }
            }}
            onEditRequest={onAssignmentEditRequest}
            isSelected={selectedAssignmentId === assignment.id}
            onResize={onAssignmentResize}
            onResizeStart={onAssignmentResizeStart}
            onMoveToOnDeck={onMoveToOnDeck}
            onAssignmentContextMenu={onAssignmentContextMenu}
            isOtherDragging={!!activeDragId && activeDragId !== assignment.id}
            colorMap={colorMap}
            nameMap={nameMap}
            splitRole={splitRoles.get(assignment.id) ?? null}
            isConnectionSource={connectingFromId === assignment.id}
            isConnectionTarget={!!connectingFromId && connectingFromId !== assignment.id}
            readOnly={readOnly}
            showNotes={showNotes}
          />
        ))}
      </div>

      {/* Drop indicator when hovering any drag over this row */}
      {(isOver) && (
        <div className="absolute inset-0 border-2 border-dashed border-primary/50 pointer-events-none" />
      )}
    </div>
  );
}
